import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import { clusterDivStyle } from "../../styles/commonStyles";
import { Marker, Popup } from "react-leaflet";
import { customIcon } from "./mapUtils";
import { Box, Button, Stack, Typography } from "@mui/material";
import { formattedDate } from "../../utils/dateUtils";
import { isValidFieldNotePosition } from "../../utils/fieldNotebookUtils";

const getDiseaseMarkerStatus = (classification = "") => {
  const normalizedClassification = classification.toLowerCase();

  if (
    !normalizedClassification ||
    normalizedClassification.includes("non classificata")
  ) {
    return "default";
  }

  if (
    normalizedClassification.includes("sane") ||
    normalizedClassification.includes("assenza") ||
    normalizedClassification.includes("nessun")
  ) {
    return "healthy";
  }

  return "critical";
};

const formatCoordinate = (value) => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue.toFixed(6) : "-";
};

const MapDiseaseMarkerCluster = ({ images, reports, onImageClick }) => {
  const diseaseImages = Array.isArray(images)
    ? images.filter((image) => Array.isArray(image?.position))
    : [];
  const diseaseReports = Array.isArray(reports)
    ? reports.filter(
        (report) =>
          report?.note_type === "DISEASE_REPORT" &&
          isValidFieldNotePosition(report?.position),
      )
    : [];

  if (diseaseImages.length === 0 && diseaseReports.length === 0) {
    return null;
  }

  return (
    <MarkerClusterGroup
      chunkedLoading
      spiderfyOnMaxZoom={true}
      disableClusterAtZoom={20}
      showCoverageOnHover={false}
      iconCreateFunction={(cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="${clusterDivStyle}"><span>${count}</span></div>`,
          className: "custom-disease-cluster",
          iconSize: L.point(40, 40),
        });
      }}
    >
      {diseaseImages.map((image) => {
        return (
          <Marker
            key={image.id}
            position={image.position}
            icon={customIcon(
              "disease",
              getDiseaseMarkerStatus(image.classification),
            )}
          >
            <Popup>
              <Box minWidth={260} display="flex" flexDirection="column" gap={1}>
                <Stack direction="column" spacing={0.5}>
                  <Typography fontWeight="bold" fontSize="14px">
                    {image.classification || "Classificazione non disponibile"}
                  </Typography>
                  <Typography fontSize="12px" color="text.secondary">
                    Classificazione FieldData
                  </Typography>
                  <Typography fontSize="12px" color="text.secondary">
                    Acquisizione: {formattedDate(image.timestamp, true)}
                  </Typography>
                </Stack>
                <Typography fontSize="12px" sx={{ wordBreak: "break-word" }}>
                  <strong>File:</strong> {image.imageName}
                </Typography>
                <Typography fontSize="12px" color="text.secondary">
                  <strong>Coordinate fotocamera:</strong>{" "}
                  {formatCoordinate(image.position?.[0])},{" "}
                  {formatCoordinate(image.position?.[1])}
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#eef3f1",
                    borderRadius: "4px",
                    color: "#3d4a46",
                    fontSize: "12px",
                    lineHeight: 1.35,
                    p: 1,
                  }}
                >
                  La posizione corrisponde al punto di scatto esposto dall'API
                  FieldData.
                </Box>
                {onImageClick && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onImageClick(image.id)}
                    sx={{
                      alignSelf: "flex-start",
                      mt: 0.5,
                      backgroundColor: "#216557",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#17483e" },
                    }}
                  >
                    Vedi foto
                  </Button>
                )}
              </Box>
            </Popup>
          </Marker>
        );
      })}
      {diseaseReports.map((report) => (
        <Marker
          key={`field-note-${report.id}`}
          position={report.position}
          icon={customIcon("disease", "critical")}
        >
          <Popup>
            <Box minWidth={240} display="flex" flexDirection="column" gap={1}>
              <Typography fontWeight="bold" fontSize="14px">
                {report.title || "Segnalazione malattia"}
              </Typography>
              <Typography fontSize="12px" color="error">
                {report.disease?.name || "Malattia non specificata"}
              </Typography>
              <Typography fontSize="12px" color="text.secondary">
                Segnalazione manuale
              </Typography>
              <Typography fontSize="12px" color="text.secondary">
                Creata: {formattedDate(report.created_at, true)}
              </Typography>
              {report.description && (
                <Typography fontSize="12px">{report.description}</Typography>
              )}
            </Box>
          </Popup>
        </Marker>
      ))}
    </MarkerClusterGroup>
  );
};

export default MapDiseaseMarkerCluster;
